import React from 'react';
import {
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { withBrandName } from '../../../config/appConfig';
import { VARIANCE_HIGHLIGHT_BG } from '../utils/reconConstants';
import { formatAedAmount, formatDocumentType, formatReconDate } from '../utils/formatters';
import CompactAmount from './CompactAmount';

const headerSx = {
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
  bgcolor: 'background.paper',
  position: 'sticky',
  top: 64,
  zIndex: 2,
  borderBottom: 1,
  borderColor: 'divider',
};
const cellSx = { fontSize: 12, fontVariantNumeric: 'tabular-nums' };
const moneyCellSx = { ...cellSx, whiteSpace: 'nowrap', maxWidth: 180 };

// Marmin document statuses are free-form (overall_status); tone them loosely.
const statusStyle = (status) => {
  const s = (status ?? '').toUpperCase();
  if (!s || s === 'N/A' || s === 'NA') return { bg: '#F3F4F6', text: '#6B7280' };
  if (/APPROV|CLEAR|COMPLET|ACCEPT/.test(s)) return { bg: '#DCFCE7', text: '#166534' };
  if (/REJECT|FAIL|ERROR/.test(s)) return { bg: '#FEE2E2', text: '#991B1B' };
  if (s) return { bg: '#FEF3C7', text: '#92400E' };
  return { bg: '#F3F4F6', text: '#6B7280' };
};

const varianceFields = (row) => new Set((row.variance_details ?? []).map((d) => d.field));

const compareTooltip = (erpValue, marminValue, tErp, tMarmin) => `${tErp}: ${erpValue} → ${tMarmin}: ${marminValue}`;

const statusLabel = (value) => value || '—';

const BADGES = {
  sales_invoices: { label: 'INV', bg: 'rgba(25, 118, 210, 0.12)', color: '#1565C0' },
  sales_credit_notes: { label: 'CN', bg: '#FEE2E2', color: '#B91C1C' },
  purchases_invoices: { label: 'PINV', bg: '#F1F5F9', color: '#475569' },
  purchases_credit_notes: { label: 'PCN', bg: '#FEE2E2', color: '#B91C1C' },
};

const ResultsTable = ({ results, loading, totalResults, page, pageSize, onPageChange, ledger = 'ar' }) => {
  const { reconciliation: r } = useTranslatedMessages();
  const t = r.table ?? {};
  const marminLabel = withBrandName(t.marmin ?? '{brandName}');

  const from = totalResults === 0 ? 0 : page * pageSize + 1;
  const to = page * pageSize + results.length;
  const hasNext = to < totalResults;

  return (
    // overflow visible so sticky thead sticks to the page (overflow-x:auto ancestors break it).
    // CompactAmount caps pathological widths; minWidth still allows modest horizontal page scroll.
    <Paper variant="outlined" sx={{ overflow: 'visible' }}>
      <Table size="small" sx={{ minWidth: 860 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>
              {ledger === 'ap' ? (t.supplier_doc ?? 'Supplier Doc #') : (t.invoice_number ?? 'Invoice #')}
            </TableCell>
            <TableCell sx={headerSx}>{t.supplier_tin ?? 'Supplier TIN'}</TableCell>
            <TableCell sx={headerSx}>{t.document_type ?? 'Document type'}</TableCell>
            <TableCell sx={headerSx}>{t.date ?? 'Date'}</TableCell>
            <TableCell sx={headerSx} align="right">
              {t.gross_value ?? 'Gross Value'}
            </TableCell>
            <TableCell sx={headerSx} align="right">
              {t.vat_amount ?? 'VAT Amount'}
            </TableCell>
            <TableCell sx={headerSx}>{t.status ?? 'Status'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && results.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary', fontSize: 13 }}>
                {t.empty ?? 'No rows in this bucket for the current filters.'}
              </TableCell>
            </TableRow>
          )}
          {results.map((row) => {
            const fields = varianceFields(row);
            const erpOrphan = row.classification === 'ERP_ORPHAN';
            const shownDate = erpOrphan ? row.erp_invoice_date : row.marmin_invoice_date;
            const shownGross = erpOrphan ? row.erp_gross_value_aed : row.marmin_gross_value;
            const shownVat = erpOrphan ? row.erp_vat_value_aed : row.marmin_vat_value;
            const shownStatus = erpOrphan ? row.compliance_status || 'N/A' : row.compliance_status;
            const status = statusStyle(shownStatus);
            const badge = BADGES[row.document_type];
            const dateHl = fields.has('invoice_date');
            const grossHl = fields.has('gross_value');
            const vatHl = fields.has('vat_value');
            const statusHl = fields.has('status');
            const tip = (erpValue, marminValue) => compareTooltip(erpValue, marminValue, t.erp ?? 'ERP', marminLabel);
            return (
              <TableRow key={row.result_id ?? `${row.classification}-${row.invoice_number}`} hover>
                <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {badge && (
                      <Chip
                        size="small"
                        label={badge.label}
                        sx={{
                          height: 16,
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: 0.4,
                          bgcolor: badge.bg,
                          color: badge.color,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    )}
                    <span>{row.invoice_number || '—'}</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>{row.supplier_tin ?? row.tin}</TableCell>
                <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>
                  {formatDocumentType(row.document_type)}
                </TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', ...(dateHl && { bgcolor: VARIANCE_HIGHLIGHT_BG }) }}>
                  <Tooltip
                    title={tip(formatReconDate(row.erp_invoice_date), formatReconDate(row.marmin_invoice_date))}
                    arrow
                  >
                    <span>{formatReconDate(shownDate)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ ...moneyCellSx, ...(grossHl && { bgcolor: VARIANCE_HIGHLIGHT_BG }) }}>
                  <CompactAmount
                    value={shownGross}
                    abs
                    compareTooltip={tip(
                      formatAedAmount(row.erp_gross_value_aed),
                      formatAedAmount(row.marmin_gross_value),
                    )}
                    sx={{ justifyContent: 'flex-end', fontSize: 12 }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ ...moneyCellSx, ...(vatHl && { bgcolor: VARIANCE_HIGHLIGHT_BG }) }}>
                  <CompactAmount
                    value={shownVat}
                    abs
                    compareTooltip={tip(formatAedAmount(row.erp_vat_value_aed), formatAedAmount(row.marmin_vat_value))}
                    sx={{ justifyContent: 'flex-end', fontSize: 12 }}
                  />
                </TableCell>
                <TableCell sx={{ ...cellSx, ...(statusHl && { bgcolor: VARIANCE_HIGHLIGHT_BG }) }}>
                  <Tooltip title={tip(statusLabel(row.erp_status), statusLabel(row.compliance_status))} arrow>
                    <Chip
                      size="small"
                      label={statusLabel(shownStatus)}
                      sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: status.bg, color: status.text }}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {`${t.showing ?? 'Showing'} ${from}–${to} ${t.of ?? 'of'} ${Number(totalResults).toLocaleString()}`}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
            {t.previous ?? 'Previous'}
          </Button>
          <Button size="small" variant="outlined" disabled={!hasNext} onClick={() => onPageChange(page + 1)}>
            {t.next ?? 'Next'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ResultsTable;
